import React from "react";
import PropTypes from "prop-types";

const CateringOrderDetailsModal = ({ 
  isOpen, 
  onClose, 
  orderDetails 
}) => {
  console.log("Modal Props - isOpen:", isOpen, "orderDetails:", orderDetails);
  
  if (!isOpen || !orderDetails) return null;

  // Calculate total add-ons price
  const totalAddOnsPrice = orderDetails.serviceItems?.reduce((total, item) => {
    const addOnPrice = parseFloat(item.add_on_price || item.menu_item?.add_on_price || 0);
    return total + addOnPrice;
  }, 0) || 0;

  // Total price is authoritative from the API (includes everything)
  const totalPrice = parseFloat(orderDetails.order_total_price || orderDetails.total_price || 0);
  
  // Payment processing fee
  const paymentFee = parseFloat(orderDetails.paymentProcessingFee || 0);
  
  // Calculate base price by subtracting add-ons and fees from total
  // Base Price = Total - Add-ons - Payment Fee
  const basePrice = totalPrice - totalAddOnsPrice - paymentFee;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-700 via-slate-800 to-slate-900 text-white p-6">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-2">Order Details</h2>
              <div className="space-y-1 text-sm">
                <p className="font-semibold text-slate-100">Order #{orderDetails.orderNo}</p>
                <p className="text-slate-200">{orderDetails.restaurant}</p>
                <p className="text-slate-300">{orderDetails.serviceItemName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="ml-4 p-2 hover:bg-white/20 rounded-full transition-colors"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Customer & Event Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 pb-6 border-b border-gray-200">
            <div>
              <h3 className="text-sm font-semibold text-slate-600 uppercase mb-2 tracking-wide">Customer Information</h3>
              {orderDetails.customerName && (
                <p className="text-gray-700 mb-1">
                  <span className="font-medium text-slate-600">Name:</span> {orderDetails.customerName}
                </p>
              )}
              <p className="text-gray-700">
                <span className="font-medium text-slate-600">Order Placed:</span> {orderDetails.orderDate || "N/A"}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-600 uppercase mb-2 tracking-wide">Event Details</h3>
              <p className="text-gray-700 mb-1">
                <span className="font-medium text-slate-600">Date:</span> {orderDetails.serviceItemDate || "N/A"}
              </p>
              <p className="text-gray-700 mb-1">
                <span className="font-medium text-slate-600">Time:</span> {orderDetails.serviceItemSlot || "N/A"}
              </p>
              <p className="text-gray-700">
                <span className="font-medium text-slate-600">PAX:</span> {orderDetails.noOfPax}
              </p>
            </div>
          </div>

          {/* Delivery Address Section */}
          {orderDetails.addressDetails && (
            <div className="mb-6 pb-6 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-slate-600 uppercase mb-3 tracking-wide">Delivery Address</h3>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                {orderDetails.userType === "customer" ? (
                  // Customer Address Format
                  <>
                    {orderDetails.addressDetails.addressType && (
                      <p className="text-gray-700 mb-2">
                        <span className="inline-block px-2 py-1 bg-slate-200 text-slate-700 text-xs font-semibold rounded uppercase">
                          {orderDetails.addressDetails.addressType}
                        </span>
                      </p>
                    )}
                    
                    {/* For House Type */}
                    {orderDetails.addressDetails.addressType?.toLowerCase() === "house" && (
                      <>
                        {orderDetails.addressDetails.houseNo && (
                          <p className="text-gray-700 mb-1">
                            <span className="font-medium text-slate-600">House No:</span> {orderDetails.addressDetails.houseNo}
                          </p>
                        )}
                        {orderDetails.addressDetails.street && (
                          <p className="text-gray-700 mb-1">
                            <span className="font-medium text-slate-600">Street:</span> {orderDetails.addressDetails.street}
                          </p>
                        )}
                        {orderDetails.addressDetails.block && (
                          <p className="text-gray-700 mb-1">
                            <span className="font-medium text-slate-600">Block:</span> {orderDetails.addressDetails.block}
                          </p>
                        )}
                        {orderDetails.addressDetails.avenue && (
                          <p className="text-gray-700 mb-1">
                            <span className="font-medium text-slate-600">Avenue:</span> {orderDetails.addressDetails.avenue}
                          </p>
                        )}
                      </>
                    )}

                    {/* For Apartment Type */}
                    {orderDetails.addressDetails.addressType?.toLowerCase() === "apartment" && (
                      <>
                        {orderDetails.addressDetails.building && (
                          <p className="text-gray-700 mb-1">
                            <span className="font-medium text-slate-600">Building:</span> {orderDetails.addressDetails.building}
                          </p>
                        )}
                        {orderDetails.addressDetails.floor && (
                          <p className="text-gray-700 mb-1">
                            <span className="font-medium text-slate-600">Floor:</span> {orderDetails.addressDetails.floor}
                          </p>
                        )}
                        {orderDetails.addressDetails.apartmentNo && (
                          <p className="text-gray-700 mb-1">
                            <span className="font-medium text-slate-600">Apartment No:</span> {orderDetails.addressDetails.apartmentNo}
                          </p>
                        )}
                        {orderDetails.addressDetails.street && (
                          <p className="text-gray-700 mb-1">
                            <span className="font-medium text-slate-600">Street:</span> {orderDetails.addressDetails.street}
                          </p>
                        )}
                        {orderDetails.addressDetails.block && (
                          <p className="text-gray-700 mb-1">
                            <span className="font-medium text-slate-600">Block:</span> {orderDetails.addressDetails.block}
                          </p>
                        )}
                      </>
                    )}

                    {/* For Office Type */}
                    {orderDetails.addressDetails.addressType?.toLowerCase() === "office" && (
                      <>
                        {orderDetails.addressDetails.companyName && (
                          <p className="text-gray-700 mb-1">
                            <span className="font-medium text-slate-600">Company:</span> {orderDetails.addressDetails.companyName}
                          </p>
                        )}
                        {orderDetails.addressDetails.building && (
                          <p className="text-gray-700 mb-1">
                            <span className="font-medium text-slate-600">Building:</span> {orderDetails.addressDetails.building}
                          </p>
                        )}
                        {orderDetails.addressDetails.floor && (
                          <p className="text-gray-700 mb-1">
                            <span className="font-medium text-slate-600">Floor:</span> {orderDetails.addressDetails.floor}
                          </p>
                        )}
                        {orderDetails.addressDetails.street && (
                          <p className="text-gray-700 mb-1">
                            <span className="font-medium text-slate-600">Street:</span> {orderDetails.addressDetails.street}
                          </p>
                        )}
                        {orderDetails.addressDetails.block && (
                          <p className="text-gray-700 mb-1">
                            <span className="font-medium text-slate-600">Block:</span> {orderDetails.addressDetails.block}
                          </p>
                        )}
                      </>
                    )}

                    {/* Contact Number */}
                    {orderDetails.addressDetails.mobileNumber && (
                      <p className="text-gray-700 mt-2 pt-2 border-t border-slate-300">
                        <span className="font-medium text-slate-600">Contact:</span> {orderDetails.addressDetails.countryCode} {orderDetails.addressDetails.mobileNumber}
                      </p>
                    )}
                  </>
                ) : (
                  // Company Address Format
                  <>
                    {orderDetails.addressDetails.address1 && (
                      <p className="text-gray-700 mb-1">{orderDetails.addressDetails.address1}</p>
                    )}
                    {orderDetails.addressDetails.address2 && (
                      <p className="text-gray-700">{orderDetails.addressDetails.address2}</p>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Order Status & Payment */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 pb-6 border-b border-gray-200">
            <div>
              <h3 className="text-sm font-semibold text-slate-600 uppercase mb-2">Status</h3>
              <span className={`inline-block px-3 py-1.5 rounded-lg text-sm font-semibold shadow-sm ${
                orderDetails.orderStatus === "Submitted" ? "bg-yellow-500 text-white" :
                orderDetails.orderStatus === "Accepted" ? "bg-blue-500 text-white" :
                orderDetails.orderStatus === "Canceled" ? "bg-red-500 text-white" :
                orderDetails.orderStatus === "Paid" ? "bg-green-500 text-white" :
                orderDetails.orderStatus === "Pending Payment" ? "bg-orange-500 text-white" :
                orderDetails.orderStatus === "Delivered" ? "bg-teal-500 text-white" :
                orderDetails.orderStatus === "Preparing" ? "bg-indigo-500 text-white" :
                orderDetails.orderStatus === "On the way" ? "bg-purple-500 text-white" :
                orderDetails.orderStatus === "Ready for Dispatch" ? "bg-amber-500 text-white" :
                "bg-slate-500 text-white"
              }`}>
                {orderDetails.orderStatus}
              </span>
            </div>
            {orderDetails.paymentMethod && (
              <div>
                <h3 className="text-sm font-semibold text-slate-600 uppercase mb-2">Payment Method</h3>
                <p className="text-gray-700 font-medium">{orderDetails.paymentMethod}</p>
              </div>
            )}
          </div>

          {/* Modification Info */}
          {orderDetails.updatedByAgentId && String(orderDetails.updatedByAgentId).trim() !== "" && (
            <div className="mb-6 pb-6 border-b border-gray-200">
              <div className="bg-slate-50 border border-slate-300 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-5 h-5 text-slate-700" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                  </svg>
                  <h3 className="text-sm font-semibold text-slate-800 uppercase">Order Modified</h3>
                </div>
                <p className="text-sm text-slate-700">
                  <span className="font-medium">Modified by Agent #{orderDetails.updatedByAgentId}</span>
                  {orderDetails.updatedDatetime && (
                    <span className="ml-2 text-slate-600">on {orderDetails.updatedDatetime}</span>
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Items List */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Order Items</h3>
            <div className="space-y-3">
              {orderDetails.serviceItems && orderDetails.serviceItems.length > 0 ? (
                orderDetails.serviceItems.map((item, index) => {
                  const itemAddOnPrice = parseFloat(item.add_on_price || item.menu_item?.add_on_price || 0);
                  const quantity = parseInt(item.quantity || item.choose || 1);
                  const itemTotal = itemAddOnPrice;

                  return (
                    <div key={index} className="bg-slate-50 border border-slate-200 rounded-lg p-4 hover:bg-slate-100 transition-colors">
                      <div className="flex gap-4">
                        {/* Image */}
                        {item.menu_item?.image && (
                          <div className="flex-shrink-0">
                            <img
                              src={item.menu_item.image}
                              alt={item.menu_item.name_en || item.menu_item.name_ar}
                              className="w-16 h-16 object-cover rounded-md shadow-sm"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                        
                        {/* Item Details */}
                        <div className="flex-1">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h4 className="font-semibold text-slate-800">
                                {item.menu_item?.name_en || item.menu_item?.name_ar || "Item"}
                              </h4>
                              <p className="text-xs text-slate-500">
                                {item.category_name_en || item.category_name_ar || ""}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-medium text-slate-700">
                                Qty: {quantity}
                              </p>
                            </div>
                          </div>
                          
                          {/* Pricing */}
                          <div className="flex justify-between items-center text-sm">
                            <div className="text-slate-600">
                              {item.portion && (
                                <span className="text-xs bg-slate-200 text-slate-700 px-2 py-0.5 rounded">
                                  Portion: {item.portion}
                                </span>
                              )}
                            </div>
                            <div className="text-right">
                              {itemAddOnPrice > 0 && (
                                <p className="text-sm font-semibold text-green-600">
                                  Add-on: +{orderDetails.currency} {itemAddOnPrice.toFixed(3)}
                                </p>
                              )}
                              {itemAddOnPrice === 0 && (
                                <p className="text-sm text-slate-500">
                                  Included
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-gray-500 text-center py-4">No items found</p>
              )}
            </div>
          </div>

          {/* Notes */}
          {orderDetails.notes && orderDetails.notes.trim() !== "" && orderDetails.notes !== "N/A" && (
            <div className="mb-6 pb-6 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-slate-600 uppercase mb-2 tracking-wide">Special Instructions</h3>
              <p className="text-gray-700 bg-slate-50 border border-slate-200 p-3 rounded-lg">{orderDetails.notes}</p>
            </div>
          )}

          {/* Price Breakdown */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Price Breakdown</h3>
            <div className="space-y-2.5">
              <div className="flex justify-between text-gray-700">
                <span>Base Package Price ({orderDetails.noOfPax} PAX)</span>
                <span className="font-medium text-slate-800">{orderDetails.currency} {basePrice.toFixed(3)}</span>
              </div>
              
              {totalAddOnsPrice > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Add-ons</span>
                  <span className="font-medium text-green-600">+{orderDetails.currency} {totalAddOnsPrice.toFixed(3)}</span>
                </div>
              )}
              
              {paymentFee > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Payment Processing Fee</span>
                  <span className="font-medium text-slate-600">+{orderDetails.currency} {paymentFee.toFixed(3)}</span>
                </div>
              )}
              
              <div className="border-t border-slate-300 pt-3 mt-3">
                <div className="flex justify-between text-lg font-bold">
                  <span className="text-slate-900">Total</span>
                  <span className="text-slate-900">{orderDetails.currency} {totalPrice.toFixed(3)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 p-4 bg-slate-50">
          <button
            onClick={onClose}
            className="w-full bg-slate-700 hover:bg-slate-800 text-white font-semibold py-3 rounded-lg transition-colors shadow-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

CateringOrderDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  orderDetails: PropTypes.shape({
    orderNo: PropTypes.string,
    restaurant: PropTypes.string,
    serviceItemName: PropTypes.string,
    customerName: PropTypes.string,
    orderDate: PropTypes.string,
    serviceItemDate: PropTypes.string,
    serviceItemSlot: PropTypes.string,
    noOfPax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    orderStatus: PropTypes.string,
    paymentMethod: PropTypes.string,
    updatedByAgentId: PropTypes.string,
    updatedDatetime: PropTypes.string,
    serviceItems: PropTypes.array,
    notes: PropTypes.string,
    service_item_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    order_total_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    total_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    currency: PropTypes.string,
    paymentProcessingFee: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    userType: PropTypes.string,
    addressDetails: PropTypes.shape({
      addressType: PropTypes.string,
      block: PropTypes.string,
      street: PropTypes.string,
      avenue: PropTypes.string,
      houseNo: PropTypes.string,
      building: PropTypes.string,
      floor: PropTypes.string,
      apartmentNo: PropTypes.string,
      companyName: PropTypes.string,
      mobileNumber: PropTypes.string,
      countryCode: PropTypes.string,
      address1: PropTypes.string,
      address2: PropTypes.string,
    }),
  }),
};

export default CateringOrderDetailsModal;

